import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Send,
  Eye,
  X,
  Clock,
  Phone,
  RefreshCw,
  CalendarCheck,
  CheckCircle,
  Circle,
  ChevronDown,
} from 'lucide-react';
import { followupApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Template {
  id: string;
  name: string;
  template_type: string;
  estimated_minutes: number;
  questions?: any[];
}

interface Instance {
  id: string;
  case_id: string;
  template_id: string;
  template_name: string;
  scheduled_days: number;
  scheduled_date: string;
  status: string;
  include_call: boolean;
  call_date: string | null;
  sent_at: string | null;
  responded_at: string | null;
  token: string;
}

interface Props {
  caseId: string;
  blockId: string;
  clientEmail: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const statusLabels: Record<string, string> = {
  ausstehend: 'Geplant',
  gesendet: 'Gesendet',
  beantwortet: 'Antwort erhalten',
  abgeschlossen: 'Abgeschlossen',
};

const statusBadge: Record<string, string> = {
  ausstehend: 'badge-new',
  gesendet: 'badge-ongoing',
  beantwortet: 'badge-completed',
  abgeschlossen: 'badge-completed',
};

const statusDotColor: Record<string, string> = {
  ausstehend: '#aaa',
  gesendet: '#4A90D9',
  beantwortet: '#2E8B57',
  abgeschlossen: '#2E8B57',
};

const templateTypeBadge: Record<string, { bg: string; color: string }> = {
  standard: { bg: '#E8F0F8', color: '#4A90D9' },
  kurz: { bg: '#FFF5E0', color: '#B8860B' },
  ausfuehrlich: { bg: '#F0E8F8', color: '#7B5EA7' },
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const dayOptions = [
  { value: 30, label: '30 Tage' },
  { value: 90, label: '90 Tage' },
  { value: 180, label: '180 Tage' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function FollowUpForm({ caseId, blockId, clientEmail }: Props) {
  const { showToast } = useToast();
  // Data
  const [templates, setTemplates] = useState<Template[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedDays, setSelectedDays] = useState(30);
  const [includeCall, setIncludeCall] = useState(false);
  const [creating, setCreating] = useState(false);

  // Actions
  const [sending, setSending] = useState<string | null>(null);

  // Preview
  const [previewInstance, setPreviewInstance] = useState<Instance | null>(null);

  // Response viewer
  const [viewingResponse, setViewingResponse] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<any>(null);
  const [loadingResponse, setLoadingResponse] = useState(false);

  /* ---- Load data ---- */

  const loadData = useCallback(async () => {
    try {
      const [tplRes, instRes] = await Promise.all([
        followupApi.templates(),
        followupApi.listForCase(caseId),
      ]);
      const tpls = tplRes.data?.items || tplRes.data || [];
      setTemplates(tpls);
      if (tpls.length > 0 && !selectedTemplate) {
        setSelectedTemplate(tpls[0].id);
      }
      const insts = instRes.data?.items || instRes.data || [];
      setInstances(insts);
    } catch {
      /* keep empty arrays */
    } finally {
      setLoading(false);
    }
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ---- Create follow-up ---- */

  const handleCreate = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    try {
      await followupApi.create(caseId, {
        template_id: selectedTemplate,
        scheduled_days: selectedDays,
        include_call: includeCall,
      });
      await loadData();
    } catch {
      showToast('Follow-Up konnte nicht erstellt werden.', 'error');
    }
    setCreating(false);
  };

  /* ---- Send questionnaire ---- */

  const handleSend = async (instanceId: string) => {
    setSending(instanceId);
    try {
      await followupApi.send(instanceId);
      await loadData();
    } catch {
      showToast('Fragebogen konnte nicht gesendet werden.', 'error');
    }
    setSending(null);
  };

  /* ---- View response ---- */

  const handleViewResponse = async (instanceId: string) => {
    setViewingResponse(instanceId);
    setLoadingResponse(true);
    try {
      const res = await followupApi.getResponse(instanceId);
      setResponseData(res.data);
    } catch {
      setResponseData(null);
    }
    setLoadingResponse(false);
  };

  const closeResponseViewer = () => {
    setViewingResponse(null);
    setResponseData(null);
  };

  /* ---- Sort instances by scheduled_days ascending ---- */

  const sortedInstances = [...instances].sort(
    (a, b) => a.scheduled_days - b.scheduled_days
  );

  /* ---- Loading ---- */

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
        Follow-Up-Daten werden geladen...
      </div>
    );
  }

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */

  return (
    <div className="block-form">
      {/* ============================================================ */}
      {/* 1. Create new Follow-Up                                      */}
      {/* ============================================================ */}
      {templates.length > 0 && (
        <div className="block-form-section">
          <h4>Neues Follow-Up planen</h4>

          {/* Template selector */}
          <div className="form-group">
            <label>Vorlage</label>
            <div style={{ position: 'relative' }}>
              <select
                className="form-control"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                style={{
                  width: '100%',
                  appearance: 'none',
                  paddingRight: 32,
                }}
              >
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name} ({tpl.estimated_minutes} Min. | {tpl.template_type})
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: 'var(--text-muted)',
                }}
              />
            </div>
          </div>

          {/* Timing chips */}
          <div className="form-group">
            <label>Zeitpunkt nach Abschluss</label>
            <div className="category-chips" style={{ marginTop: 4 }}>
              {dayOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`chip ${selectedDays === opt.value ? 'chip-active' : ''}`}
                  onClick={() => setSelectedDays(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Include call checkbox */}
          <div className="form-group">
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                checked={includeCall}
                onChange={(e) => setIncludeCall(e.target.checked)}
                style={{ accentColor: 'var(--primary)', width: 16, height: 16 }}
              />
              <Phone size={14} style={{ color: 'var(--primary)' }} />
              Follow-Up Call planen
            </label>
          </div>

          {/* Create button */}
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={creating || !selectedTemplate}
          >
            <Plus size={16} /> {creating ? 'Wird erstellt...' : 'Follow-Up planen'}
          </button>
        </div>
      )}

      {/* ============================================================ */}
      {/* 2. Timeline of planned Follow-Ups                            */}
      {/* ============================================================ */}
      <div className="block-form-section" style={{ marginTop: 8 }}>
        <h4>Geplante Follow-Ups</h4>

        {sortedInstances.length === 0 ? (
          /* Empty state */
          <div
            style={{
              textAlign: 'center',
              padding: '32px 16px',
              color: 'var(--text-muted)',
            }}
          >
            <CalendarCheck
              size={40}
              style={{ marginBottom: 12, opacity: 0.4, color: 'var(--primary)' }}
            />
            <p style={{ marginBottom: 6, fontWeight: 500 }}>
              Noch keine Follow-Ups geplant
            </p>
            <p style={{ fontSize: '0.82rem' }}>
              Erstelle oben ein Follow-Up, um den Coaching-Fortschritt nachzuverfolgen.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
            {/* Vertical timeline line */}
            <div
              style={{
                position: 'absolute',
                left: 11,
                top: 20,
                bottom: 20,
                width: 2,
                background: 'var(--border)',
                zIndex: 0,
              }}
            />

            {sortedInstances.map((inst) => (
              <div
                key={inst.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '12px 0',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {/* Timeline dot */}
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--bg-card)',
                    border: `3px solid ${statusDotColor[inst.status] || '#aaa'}`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  {inst.status === 'beantwortet' || inst.status === 'abgeschlossen' ? (
                    <CheckCircle size={12} style={{ color: statusDotColor[inst.status] }} />
                  ) : (
                    <Circle size={8} style={{ color: statusDotColor[inst.status] || '#aaa' }} />
                  )}
                </div>

                {/* Card content */}
                <div
                  className="card"
                  style={{
                    flex: 1,
                    padding: '14px 18px',
                  }}
                >
                  {/* Top row: info */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: 'var(--primary-dark)',
                      }}
                    >
                      {inst.scheduled_days} Tage
                    </span>
                    <span style={{ color: 'var(--border)', fontSize: '0.8rem' }}>|</span>
                    <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>
                      {inst.template_name}
                    </span>
                    <span className={`badge ${statusBadge[inst.status] || 'badge-new'}`}>
                      {statusLabels[inst.status] || inst.status}
                    </span>
                  </div>

                  {/* Date row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      fontSize: '0.8rem',
                      color: 'var(--text-muted)',
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={12} />
                      Geplant: {fmtDate(inst.scheduled_date)}
                    </span>
                    {inst.sent_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Send size={12} />
                        Gesendet: {fmtDate(inst.sent_at)}
                      </span>
                    )}
                    {inst.responded_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle size={12} />
                        Antwort: {fmtDate(inst.responded_at)}
                      </span>
                    )}
                    {inst.include_call && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Phone size={12} />
                        {inst.call_date ? `Call: ${fmtDate(inst.call_date)}` : 'Call geplant'}
                      </span>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {inst.status === 'ausstehend' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setPreviewInstance(inst)}
                      >
                        <Eye size={14} /> Fragebogen ansehen & senden
                      </button>
                    )}

                    {inst.status === 'gesendet' && (
                      <>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: '0.82rem',
                            color: 'var(--text-muted)',
                            padding: '4px 8px',
                          }}
                        >
                          <Clock size={14} /> Warte auf Antwort...
                        </span>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setPreviewInstance(inst)}
                        >
                          <Eye size={14} /> Fragebogen ansehen
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleSend(inst.id)}
                          disabled={sending === inst.id}
                        >
                          <RefreshCw size={14} />
                          {sending === inst.id ? 'Wird gesendet...' : 'Erneut senden'}
                        </button>
                      </>
                    )}

                    {inst.status === 'beantwortet' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleViewResponse(inst.id)}
                      >
                        <Eye size={14} /> Antworten anzeigen
                      </button>
                    )}

                    {inst.include_call && !inst.call_date && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          showToast('Call-Planung wird in einer zukünftigen Version verfügbar sein.', 'info')
                        }
                      >
                        <Phone size={14} /> Call planen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* 3. Questionnaire Preview Modal                               */}
      {/* ============================================================ */}
      {previewInstance && (
        <div className="modal-overlay" onClick={() => setPreviewInstance(null)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900, width: '95vw', height: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>
                  Fragebogen-Vorschau (so sieht es der Klient)
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {previewInstance.template_name} — {previewInstance.scheduled_days} Tage
                </p>
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPreviewInstance(null)}
                style={{ padding: 6 }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', background: 'var(--bg)' }}>
              <iframe
                src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/followup/respond/${previewInstance.token}?preview=1`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Fragebogen-Vorschau"
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 24px', borderTop: '1px solid var(--border-light)' }}>
              <button className="btn btn-secondary" onClick={() => setPreviewInstance(null)}>
                Schließen
              </button>
              {previewInstance.status === 'ausstehend' && clientEmail && (
                <button
                  className="btn btn-primary"
                  disabled={sending === previewInstance.id}
                  onClick={async () => {
                    const instId = previewInstance.id;
                    setSending(instId);
                    try {
                      await followupApi.send(instId);
                      await loadData();
                      setPreviewInstance(null);
                      showToast('Fragebogen wurde erfolgreich per E-Mail gesendet!', 'success');
                    } catch {
                      showToast('Fragebogen konnte nicht gesendet werden.', 'error');
                    }
                    setSending(null);
                  }}
                >
                  <Send size={14} /> {sending === previewInstance.id ? 'Wird gesendet...' : `An ${clientEmail} senden`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* 4. Response Viewer Modal                                     */}
      {/* ============================================================ */}
      {viewingResponse && (
        <div className="modal-overlay" onClick={closeResponseViewer}>
          <div
            className="modal modal-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '85vh' }}
          >
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Follow-Up Antworten</h3>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '0.82rem',
                    color: 'var(--text-muted)',
                  }}
                >
                  {(() => {
                    const inst = instances.find((i) => i.id === viewingResponse);
                    return inst
                      ? `${inst.template_name} - ${inst.scheduled_days} Tage`
                      : '';
                  })()}
                </p>
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={closeResponseViewer}
                style={{ padding: 6 }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '0 24px 24px' }}>
              {loadingResponse ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 32,
                    color: 'var(--text-muted)',
                  }}
                >
                  Antworten werden geladen...
                </div>
              ) : !responseData || !responseData.answers ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: 32,
                    color: 'var(--text-muted)',
                  }}
                >
                  Keine Antworten vorhanden.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {(responseData.answers as any[]).map((answer: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        background: 'var(--bg)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '14px 16px',
                        borderLeft: '3px solid var(--primary)',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: 'var(--text-light)',
                          marginBottom: 8,
                        }}
                      >
                        {answer.question || answer.label || `Frage ${idx + 1}`}
                      </div>

                      {/* Scale answer */}
                      {answer.type === 'scale' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            style={{
                              flex: 1,
                              height: 10,
                              background: 'var(--border-light)',
                              borderRadius: 5,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${((answer.value as number) / (answer.max || 10)) * 100}%`,
                                height: '100%',
                                background:
                                  'linear-gradient(90deg, var(--primary), var(--primary-light))',
                                borderRadius: 5,
                                transition: 'width 0.3s',
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: '1rem',
                              color: 'var(--primary)',
                              minWidth: 36,
                              textAlign: 'right',
                            }}
                          >
                            {answer.value}/{answer.max || 10}
                          </span>
                        </div>
                      )}

                      {/* Select answer */}
                      {answer.type === 'select' && (
                        <span className="badge badge-ongoing" style={{ fontSize: '0.85rem' }}>
                          {answer.value}
                        </span>
                      )}

                      {/* Textarea / text answer */}
                      {(answer.type === 'textarea' || answer.type === 'text') && (
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: 'var(--text)',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {answer.value}
                        </div>
                      )}

                      {/* Fallback for unknown types */}
                      {!['scale', 'select', 'textarea', 'text'].includes(answer.type) && (
                        <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                          {String(answer.value)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
